import React, {useMemo, useState, useEffect} from 'react';
import TableContainer from "../../../Common/Tabledata/TableContainer";
import {Button, Card, Col, Dropdown, Form, Row, Alert as BootstrapAlert} from 'react-bootstrap';
import {ToastContainer, toast} from 'react-toastify';
import NoSearchResult from '../../../Common/Tabledata/NoSearchResult';
import {useTranslation} from "react-i18next";
import {COLORS} from "../../../Common/constants/colors";
import ReceiptModal from "../../TransactionList/Components/ReceiptModal";
import {DeleteItem} from "../../../Common/DeleteModal";
import {Link} from "react-router-dom";
import {useSelector} from "react-redux";
import {dashboardAPI} from "../../../services/api";
import {mockStores} from "../../../data/stores";

const AlertTable = () => {
    const {t} = useTranslation();
    
    // Check if user is demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    // For demo users, use Redux state
    const reduxAlerts = useSelector((state:any) => state.Transactions.alerts);
    const reduxTransactions = useSelector((state: any) => state.Transactions.transactions);
    
    const [alerts, setAlerts] = useState<Alert[]>(isDemoUser ? reduxAlerts : []);
    const [transactions, setTransactions] = useState<any[]>(isDemoUser ? reduxTransactions : []);
    const [stores, setStores] = useState<Store[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedTransaction, setSelectedTransaction] = useState<any>();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState<any>(null);

    // Fetch stores for store names
    useEffect(() => {
        if (isDemoUser) {
            setStores(mockStores);
            return;
        }
        const fetchStores = async () => {
            try {
                const response: any = await dashboardAPI.getStores();
                const storesData = Array.isArray(response) ? response : (response?.data || []);
                setStores(storesData.map((s: any) => ({
                    id: s.id || '',
                    name: s.name || '',
                    city: s.city || '',
                    province: s.province || '',
                    phone: s.phone || '',
                    address: s.address || '',
                    active: s.active !== false,
                    createdOn: s.createdOn || ''
                })));
            } catch (error) {
                console.error('Failed to fetch stores:', error);
            }
        };
        fetchStores();
    }, [isDemoUser]);

    // Fetch alerts from backend for real users
    useEffect(() => {
        if (isDemoUser) {
            // Use Redux state for demo users
            setAlerts(reduxAlerts);
            setTransactions(reduxTransactions);
            return;
        }

        const fetchAlerts = async () => {
            setIsLoading(true);
            setError(null);
            try {
                console.log('📋 Fetching alerts from backend...');
                const response: any = await dashboardAPI.getAlerts({ page: 0, size: 1000 }); // Fetch all for now
                
                const alertsData = response?.content || [];
                console.log('✅ Alerts received:', alertsData.length, 'alerts');
                
                // Enrich alerts with store names
                const enrichedAlerts = alertsData.map((alert: Alert) => {
                    const store = stores.find(s => s.id === alert.store?.id);
                    return {
                        ...alert,
                        store: store || alert.store
                    };
                });
                
                setAlerts(enrichedAlerts);
            } catch (error: any) {
                console.error('❌ Failed to fetch alerts:', error);
                setError(error?.response?.data?.message || error?.message || 'Failed to fetch alerts');
                setAlerts([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAlerts();
    }, [isDemoUser, stores]);

    const handleEditAlert = (alert: Alert) => {
        setSelectedAlert(alert);
    };

    const handleCloseAlert = async (alert: Alert) => {
        if (isDemoUser) {
            // For demo, just update local state
            setAlerts(prev => prev.map(a => a.id === alert.id ? { ...a, status: 'closed' as AlertStatus } : a));
            toast.success(t('Alerts.alert_closed') || 'Alert closed');
            return;
        }

        try {
            await dashboardAPI.updateAlertStatus(alert.id, 'closed');
            // Refresh alerts
            const response: any = await dashboardAPI.getAlerts({ page: 0, size: 1000 });
            const alertsData = response?.content || [];
            const enrichedAlerts = alertsData.map((a: Alert) => {
                const store = stores.find(s => s.id === a.store?.id);
                return { ...a, store: store || a.store };
            });
            setAlerts(enrichedAlerts);
            toast.success(t('Alerts.alert_closed') || 'Alert closed');
        } catch (error: any) {
            console.error('Failed to close alert:', error);
            toast.error(error?.response?.data?.message || error?.message || 'Failed to close alert');
        }
    };



    const DefaultColumnFilter = ({column: {filterValue, setFilter}}: any) => (
        <Form.Control
            size="sm"
            type="text"
            placeholder={t('TransactionList.filter')}
            value={filterValue ?? ''}
            onChange={(e) => setFilter(e.target.value)}
        />
    );

    const SelectColumnFilter = ({
                                    column: { filterValue, setFilter, preFilteredRows, id }
                                }: {
        column: any;
        labelMapper?: (value: string) => string;
    }) => {
        const options = useMemo(() => {
            const opts = new Set();
            preFilteredRows.forEach((row: any) => opts.add(row.values[id]));
            return [...opts.values() as any] as string[];
        }, [id, preFilteredRows]);

        return (
            <Form.Select
                size="sm"
                value={filterValue || ''}
                onChange={(e) => setFilter(e.target.value || undefined)}
            >
                <option value="">{t('TransactionList.all')}</option>
                {options.map((option: string, i: number) => (
                    <option key={i} value={option}>
                        {getStatus(option)}
                    </option>
                ))}
            </Form.Select>
        );
    };

    const getStatus = (status: string)=>{
        switch (status) {
            case "closed":
                return t('Alerts.closed')
            case "in_review":
                return t('Alerts.in_review')
            default:
                return t('Alerts.open')
        }

    }

    const columns = useMemo(
        () => [

            {
                Header: t('StoreList.store_name'),
                accessor: (row: any) => row.store?.name || "",
                id: "store",
                Filter: DefaultColumnFilter,
                isSortable: false,
                Cell: ({ value }: any) => (
                    <div>{value.split(" ")[0]}</div>
                )
            },
            {
                Header: t('TransactionList.cashier_id'),
                accessor: "cashier",
                Filter: DefaultColumnFilter,
                Cell: ({ row }: any) => {
                    const tx = transactions.find(
                        (tx: any) => tx.receiptNumber === row.original.receiptNumber
                    );
                    return tx ? tx.cashier : "-";
                },
            },
            {
                Header: t('TransactionList.receipt_no'),
                accessor: "receiptNumber",
                Filter: DefaultColumnFilter,
                Cell: ({value, row}: any) => (
                    <Button
                        variant="link"
                        style={{padding: 0, fontSize: "0.875rem", color: COLORS.LINK}}
                        onClick={() =>{
                            const transaction =
                                transactions.filter((transaction:any) => transaction.receiptNumber === row.original.receiptNumber)[0];
                            setSelectedTransaction(transaction)
                        }}
                    >
                        {value}
                    </Button>
                ),

            },
            {
                Header: t('ScenarioList.scenario_name'),
                accessor: "scenarioName",
                Filter: DefaultColumnFilter,
                Cell: ({ value, row }: any) => (
                    <Link to={`/scenario/${row.original.scenarioId}`} style={{ color: COLORS.LINK}}>
                        <div style={{
                            maxWidth: 180,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }} title={value}>
                            {value}
                        </div>
                    </Link>
                )
            },
            {
                Header: t('RuleList.rule_name'),
                accessor: "ruleName",
                Filter: DefaultColumnFilter,
                Cell: ({ value, row }: any) => (
                    <Link to={`/rule/${row.original.ruleId}`} style={{ color: COLORS.LINK}}>
                        <div style={{
                            maxWidth: 180,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }} title={value}>
                            {value}
                        </div>
                    </Link>
                )
            },
            {
                Header: t('Alerts.alerted_amount'),
                accessor: "alerted_amount",
                Filter: false,
                isSortable: false,
                Cell: ({ row }: any) => {
                    const tx = transactions.find(
                        (tx: any) => tx.receiptNumber === row.original.receiptNumber
                    );
                    if (!tx) return "-";

                    const alertedTotal = tx.items
                        .filter((item: any) => item.alerts && item.alerts.length > 0)
                        .reduce((sum: number, item: any) => sum + item.total, 0);

                    return `₺${alertedTotal.toFixed(2)}`;
                }
            },
            {
                Header: t('Alerts.alerted_items'),
                accessor: "alerted_item_count",
                Filter:false,
                isSortable: false,
                Cell: ({ row }: any) => {
                    const tx = transactions.find((tx:any) => tx.receiptNumber === row.original.receiptNumber);
                    return tx?.items?.filter((item:any) => item.alerts?.length)?.length || 0;
                }
            },
            {
                Header: t('RuleList.created_on'),
                accessor: "date",
                Filter: false,
                isSortable: true,
            },
            {
                Header: t('Alerts.reviewer'),
                accessor: "reviewedBy",
                Filter: DefaultColumnFilter,
                isSortable: false
            },
            {
                Header: t('Alerts.status'),
                accessor: "status",
                Filter: SelectColumnFilter,
                isSortable: false,
                Cell: (cell:any) => {
                    switch (cell.row.original.status) {
                        case "closed":
                            return <span
                                style={{backgroundColor: COLORS.SUCCESS}}
                                className="badge text-white p-2">{getStatus(cell.row.original.status)}</span>
                        case "in_review":
                            return <span
                                style={{backgroundColor: COLORS.WARNING}}
                                className="badge text-black p-2">{getStatus(cell.row.original.status)}</span>
                        case "new":
                            return <span
                                style={{backgroundColor: COLORS.ERROR}}
                                className="badge text-white text-danger p-2">{getStatus(cell.row.original.status)}</span>
                    }
                }
            },
            {
                Header: t('Alerts.action'),
                accessor: "action",
                Filter: false,
                disableSortBy: true,
                isSortable: false,
                Cell: (cell: any) => {
                    const item = cell.row.original;
                    const isClosed = item.status === "closed";

                    return (
                        <Dropdown>
                            <Dropdown.Toggle as="button" className="btn btn-primary btn-sm arrow-none">
                                <i className="las la-ellipsis-h align-middle fs-18"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="dropdown-menu-end">
                                <li>
                                    <Dropdown.Item as={Link} to={`/alert/${cell.row.original.id}`}>
                                        <i className="las la-eye fs-18 align-middle me-2 text-muted"></i>
                                        {t('Alerts.view')}
                                    </Dropdown.Item>
                                </li>

                                <li>
                                    <Dropdown.Item
                                        onClick={() => !isClosed && handleCloseAlert(item)}
                                        disabled={isClosed}
                                    >
                                        <i className="las la-check fs-18 align-middle me-2 text-muted"></i>
                                        {t('Alerts.close')}
                                    </Dropdown.Item>
                                </li>

                                <li className="dropdown-divider"></li>

                                <li>
                                    <Dropdown.Item className="remove-item-btn"
                                                   onClick={() => handleEditAlert(item)}>
                                        <i className="las la-trash-alt fs-18 align-middle me-2 text-muted"></i>
                                        {t('Alerts.delete')}
                                    </Dropdown.Item>
                                </li>
                            </Dropdown.Menu>
                        </Dropdown>
                    );
                },
                getHeaderProps: () => ({
                    style: {
                        position: 'sticky',
                        right: 0,
                        background: '#fff',
                        zIndex: 2,
                        minWidth: 100,
                    }
                }),
                getCellProps: () => ({
                    style: {
                        position: 'sticky',
                        right: 0,
                        background: '#fff',
                        zIndex: 1,
                        minWidth: 100,
                    }
                }),
            },
        ], [t])
    return (
        <React.Fragment>
            {error && (
                <Row>
                    <Col>
                        <BootstrapAlert variant="danger" onClose={() => setError(null)} dismissible>
                            {error}
                        </BootstrapAlert>
                    </Col>
                </Row>
            )}
            <Row>
                <Col xl={12}>
                    <Card>
                        <Card.Body>
                            {isLoading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : alerts && alerts.length > 0 ? (
                                <TableContainer
                                    isPagination={true}
                                    columns={columns}
                                    isGlobalFilter={true}
                                    data={alerts || []}
                                    customPageSize={100}
                                    divClassName="table-card table-responsive"
                                    tableClass="table-hover table-nowrap align-middle mb-0"
                                    isBordered={false}
                                    PaginationClass="align-items-center mt-4 gy-3"
                                />
                            ) : (
                                <NoSearchResult
                                    title={t('Alerts.no_alerts_title')}
                                    message={t('Alerts.no_alerts_message')}
                                    showIcon={false}
                                />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <DeleteItem
                show={deleteModalOpen}
                rule={selectedAlert}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={() => {
                    // setAlerts((prev) => prev.filter(a => a.id !== selectedAlert.id))
                    setDeleteModalOpen(false);
                }}
            />
            <ToastContainer/>

            <ReceiptModal
                selectedTransaction={selectedTransaction}
                setSelectedTransaction={setSelectedTransaction}
            />


        </React.Fragment>
    )
}

export default AlertTable
